import { isNil, noop } from 'lodash'

import { createAction, createAsyncAction, FULFILLED, PENDING, REJECTED } from '../utils'
import {
  addProgramMembersApi,
  createProgramApi,
  getProgramsApi,
  getProgramUsageTypesApi,
  updateProgramApi,
} from '../../services/team'
import { selectTeam } from '../selectors/teamSelectors'
import { STEPS } from '../../components/teams/perks/programUtils'
import { enqueueSnackbar } from './snackbar'
import { setSelectedProgram } from './teamProgramDetails'

// ------------------------------------
// Action Types & Creators
// ------------------------------------
// TODO Rename these action names to 'foodsby/teamPrograms/**`
export const CURRENT_STEP_SET = 'foodsby/teamPrograms/CURRENT_STEP_SET'
export const PROGRAMS_SET = 'foodsby/teamPrograms/PROGRAMS_SET'
export const PROGRAM_USAGE_TYPES_SET = 'foodsby/teamPrograms/PROGRAM_USAGE_TYPES_SET'
export const PROGRAM_CREATE = 'foodsby/teamPrograms/PROGRAM_CREATE'
export const PROGRAM_MEMBERS_ADD = 'foodsby/teamPrograms/PROGRAM_MEMBERS_ADD'
export const PROGRAM_UPDATE = '/foodsby/teamPrograms/PROGRAM_UPDATE'

export const setCurrentStep = createAction(CURRENT_STEP_SET)
export const setPrograms = createAsyncAction(PROGRAMS_SET)
export const setProgramsFulfilled = createAction(FULFILLED(PROGRAMS_SET))
export const setProgramUsageTypes = createAsyncAction(PROGRAM_USAGE_TYPES_SET)
export const createProgram = createAsyncAction(PROGRAM_CREATE)
export const addProgramMembers = createAsyncAction(PROGRAM_MEMBERS_ADD)
export const updateProgramPending = createAction(PENDING(PROGRAM_UPDATE))
export const updateProgramFulfilled = createAction(FULFILLED(PROGRAM_UPDATE))

// ------------------------------------
// Thunks
// ------------------------------------
export const setCurrentStepStart = (currentStep, payload, onSuccess = noop) => {
  return async (dispatch, getState) => {
    if (currentStep === STEPS.PROGRAM_COMPLETE && payload) {
      const state = getState()
      const team = selectTeam(state)

      // TODO One action (createProgram) can be used here
      // for making both of the create program & the add program members calls
      // and delete the addProgramMembers action from this module
      const response = await dispatch(
        createProgram(createProgramApi(team.id, payload.createProgramRequest)),
      )
      await dispatch(
        addProgramMembers(addProgramMembersApi(response.value.programId, payload.programMembers)),
      )
      dispatch(loadProgramsStart(team.id))

      dispatch(
        enqueueSnackbar({
          message: 'Your subsidy has been created and is active.',
        }),
      )
      onSuccess()
    }

    return dispatch(setCurrentStep(currentStep))
  }
}

export const loadProgramsStart = (accountId, page, pageSize) => async dispatch =>
  await dispatch(setPrograms(getProgramsApi(accountId, page, pageSize)))

export const loadProgramUsageTypesStart = () => async dispatch =>
  await dispatch(setProgramUsageTypes(getProgramUsageTypesApi()))

export const updateProgramStart = (programId, updateProgramRequest) => {
  return async (dispatch, getState) => {
    const state = getState()
    const team = selectTeam(state)

    try {
      dispatch(updateProgramPending())
      const payload = {
        ...updateProgramRequest,
        neverExpires: isNil(updateProgramRequest.endDate),
      }

      await updateProgramApi(programId, payload)

      const updatedPrograms = await getProgramsApi(team.id)
      dispatch(setSelectedProgram(updatedPrograms.find(p => p.programId === programId)))
      dispatch(setProgramsFulfilled(updatedPrograms))
      dispatch(updateProgramFulfilled())
    } catch (ex) {
      dispatch(
        enqueueSnackbar({
          message:
            'Something went wrong updating the subsidy. Please try again or contact support.',
        }),
      )
      return
    }
  }
}

// ------------------------------------
// Action Handlers
// ------------------------------------
const ACTION_HANDLERS = {
  [CURRENT_STEP_SET]: (state, action) => {
    return {
      ...state,
      currentStep: action.payload,
    }
  },
  [PENDING(PROGRAMS_SET)]: state => {
    return {
      ...state,
      isLoadingPrograms: true,
    }
  },
  [FULFILLED(PROGRAMS_SET)]: (state, action) => {
    return {
      ...state,
      isLoadingPrograms: false,
      programs: action.payload,
      errorLoadingPrograms: undefined,
    }
  },
  [REJECTED(PROGRAMS_SET)]: state => {
    return {
      ...state,
      isLoadingPrograms: false,
      errorLoadingPrograms:
        'Something went wrong when creating the subsidy program. Please try again later or contact Support.',
    }
  },
  [PENDING(PROGRAM_USAGE_TYPES_SET)]: state => {
    return {
      ...state,
      isLoadingProgramUsageTypes: true,
    }
  },
  [FULFILLED(PROGRAM_USAGE_TYPES_SET)]: (state, action) => {
    return {
      ...state,
      isLoadingProgramUsageTypes: false,
      programUsageTypes: action.payload,
      errorLoadingProgramUsageTypes: undefined,
    }
  },
  [REJECTED(PROGRAM_USAGE_TYPES_SET)]: state => {
    return {
      ...state,
      isLoadingProgramUsageTypes: false,
      errorLoadingProgramUsageTypes:
        'Something went wrong when loading program usage types. Please try again later or contact Support.',
    }
  },
  [PENDING(PROGRAM_CREATE)]: state => {
    return {
      ...state,
      isCreatingProgram: true,
      errorCreatingProgram: undefined,
    }
  },
  [FULFILLED(PROGRAM_CREATE)]: (state, action) => {
    return {
      ...state,
      isCreatingProgram: false,
      createdProgram: action.payload,
      errorCreatingProgram: undefined,
    }
  },
  [REJECTED(PROGRAM_CREATE)]: (state, action) => {
    return {
      ...state,
      isCreatingProgram: false,
      errorCreatingProgram: action.payload.message,
    }
  },
  [PENDING(PROGRAM_MEMBERS_ADD)]: state => {
    return {
      ...state,
      isAddingProgramMembers: true,
      errorAddingProgramMembers: undefined,
    }
  },
  [FULFILLED(PROGRAM_MEMBERS_ADD)]: state => {
    return {
      ...state,
      isAddingProgramMembers: false,
      errorAddingProgramMembers: undefined,
    }
  },
  [REJECTED(PROGRAM_MEMBERS_ADD)]: (state, action) => {
    return {
      ...state,
      isAddingProgramMembers: false,
      errorAddingProgramMembers: action.payload.message,
    }
  },
  [PENDING(PROGRAM_UPDATE)]: state => {
    return {
      ...state,
      isUpdatingProgram: true,
    }
  },
  [FULFILLED(PROGRAM_UPDATE)]: state => {
    return {
      ...state,
      isUpdatingProgram: false,
    }
  },
}

export const initialState = {
  currentStep: undefined,
  programs: [],
  programUsageTypes: [],
  createdProgram: undefined,
  // Loading states
  isLoadingPrograms: true,
  isLoadingProgramUsageTypes: true,
  isCreatingProgram: false,
  isUpdatingProgram: false,
  // Success states
  // Error states
  errorLoadingPrograms: undefined,
  errorLoadingProgramUsageTypes: undefined,
  errorCreatingProgram: undefined,
}

export default function teamPrograms(state = initialState, action) {
  const handler = ACTION_HANDLERS[action.type]
  return handler ? handler(state, action) : state
}
